<template>
  <div
    class="so-overview d-flex"
    :class="{
      'justify-content-between': showTemplates,
      'justify-content-left': !showTemplates,
    }"
  >
    <div
      v-if="!occurrence.isCompleted"
      :class="{
        'col-4': showTemplates,
      }"
    >
      <div v-if="showTemplates" class="overview-label">
        <div class="d-flex justify-content-between">
          <div>Task Generation / Templates</div>
          <div>
            <button class="btn btn-block rounded-pill btn-primary btn-xs mt-1 pl-3 pr-3" @click="showTemplates = !showTemplates">
              Hide
            </button>
          </div>
        </div>
      </div>

      <div v-if="!showTemplates" class="templates-vertical-panel d-flex justify-content-left">
        <div class="overview-label">Task Generation / Templates</div>
        <button class="ml-2 btn btn-block rounded-pill btn-primary btn-xs mt-1 pl-3 pr-3" @click="showTemplates = !showTemplates">
          Show
        </button>
      </div>

      <div v-if="showTemplates">
        <div class="mt-2 mb-2">
          <div class="input-group mb-3">
            <input type="text" class="form-control input-sm" placeholder="Search..." v-model="search" />
            <div class="input-group-append">
              <i class="fa fa-search input-group-text pb-0"></i>
            </div>
          </div>
        </div>
        <div>
          <div class="mb-3" v-for="tInfo in templates" :key="tInfo.boardId">
            <div class="justify-content-between d-flex align-items-center mt-1 mb-2">
              <div class="board-label label">{{ tInfo.boardTitle }}</div>
              <div>
                <button
                  class="create-board-item-btn btn btn-block rounded-pill btn-info btn-xs pl-2 pr-2"
                  @click="addNewBoardItem(tInfo.boardId)"
                >
                  <i class="fas fa-plus"></i>
                  Create Task
                </button>
              </div>
            </div>
            <div v-for="template in tInfo.filteredTemplates" :key="template.id">
              <div class="so-template">
                <div class="so-template-header d-flex justify-content-between">
                  <div class="d-flex justify-content-between">
                    <div class="cursor-pointer ml-2 mr-2" v-on:click="toggleTemplate(tInfo.boardId, template.id)">
                      <i v-if="isOpened(tInfo.boardId, template.id)" class="fas fa-angle-up"></i>
                      <i v-else class="fas fa-angle-down"></i>
                    </div>

                    <div>
                      <div>{{ template.boardItemType.boardItemCategory.name }}</div>
                      <div>{{ template.title }}</div>
                      <div v-if="template.checklists && template.checklists.length">
                        <i class="fas fa-tasks"></i>
                        {{ template.checklists.length }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-b-tooltip.hover
                    :title="
                      template.requiresParam && (!occurrence.flights || !occurrence.flights.length)
                        ? 'This template requires a selected flight on the data page.'
                        : ''
                    "
                  >
                    <!-- Template doesn't require params -->
                    <button
                      v-if="!template.requiresParam"
                      class="create-template-btn btn btn-xs btn-primary rounded-pill mr-1"
                      @click="createBoardItem(tInfo.boardId, template.id)"
                      :disabled="loadingTemplateIds.includes(template.id)"
                    >
                      <i class="fas fa-plus" v-if="!loadingTemplateIds.includes(template.id)"></i>
                      <i class="fas fa-spinner fa-spin" v-if="loadingTemplateIds.includes(template.id)"></i>
                      Create
                    </button>

                    <!-- Template requires params and one flight was selected -->
                    <button
                      v-if="template.requiresParam && occurrence.flights && occurrence.flights.length === 1"
                      class="create-template-btn btn btn-xs btn-primary rounded-pill mr-1"
                      @click="createBoardItem(tInfo.boardId, template.id, occurrence.flights[0].id)"
                      :disabled="loadingTemplateIds.includes(template.id)"
                    >
                      <i class="fas fa-plus" v-if="!loadingTemplateIds.includes(template.id)"></i>
                      <i class="fas fa-spinner fa-spin" v-if="loadingTemplateIds.includes(template.id)"></i>
                      Create
                    </button>

                    <!-- Template requires params and more then one flight was selected -->
                    <button
                      v-if="template.requiresParam && (!occurrence.flights.length || (occurrence.flights && occurrence.flights.length > 1))"
                      v-bind:id="'createItemFromTemplate_' + template.id + '_' + tInfo.boardId"
                      class="create-template-btn btn btn-xs btn-primary rounded-pill mr-1"
                      :disabled="!occurrence.flights || !occurrence.flights.length"
                    >
                      <i class="fas fa-plus"></i>
                      Create
                    </button>

                    <b-popover
                      custom-class="wide-popover"
                      v-bind:ref="'createItemFromTemplateRef_' + template.id + '_' + tInfo.boardId"
                      v-bind:target="'createItemFromTemplate_' + template.id + '_' + tInfo.boardId"
                      triggers="click blur"
                      v-if="template.requiresParam && occurrence.flights && occurrence.flights.length > 1"
                    >
                      <div class="mt-1 mb-1">
                        <div class="label d-flex align-items-center justify-content-center">Choose a flight</div>

                        <table class="table table-striped">
                          <thead>
                            <tr>
                              <th scope="col"></th>
                              <th scope="col">Date</th>
                              <th scope="col">Flight</th>
                              <th scope="col">Departure-Arrival</th>
                              <th scope="col">Registration</th>
                              <th scope="col">Reason</th>
                              <th scope="col">Comment</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="flight in filteredSelectedFlights(occurrence.flights)" :key="flight.id">
                              <td>
                                <button
                                  class="create-template-btn btn btn-xs btn-primary"
                                  @click="createBoardItem(tInfo.boardId, template.id, flight.id)"
                                  :disabled="loadingTemplateIds.includes(template.id)"
                                >
                                  <i class="fas fa-plus" v-if="!loadingTemplateIds.includes(template.id)"></i>
                                  <i class="fas fa-spinner fa-spin" v-if="loadingTemplateIds.includes(template.id)"></i>
                                  Select
                                </button>
                              </td>
                              <td>{{ flight.date | formatDateWithoutHours }}</td>
                              <td>{{ flight.flightNo }}</td>

                              <td>{{ flight.departureAirport }} - {{ flight.arrivalAirport }}</td>

                              <td>{{ flight.aircraftRegistration }}</td>
                              <td>{{ flight.selectedReason }}</td>
                              <td>{{ flight.selectedReasonComment }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </b-popover>
                  </div>
                </div>

                <div class="so-template-content" v-if="isOpened(tInfo.boardId, template.id)">
                  {{ template.description }}

                  <div v-for="list in template.checklists" :key="list.id">
                    <div v-if="list.items" class="mt-1">
                      <div class="mt-2 label">{{ list.title }}</div>

                      <div class="checklist-items">
                        <div v-for="listItem in list.items" :key="listItem.title">
                          <div class="checklist-item ml-3">
                            <b-form-checkbox disabled>
                              <div class="ml-1 checkbox-label">
                                {{ listItem.title }}
                              </div>
                            </b-form-checkbox>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="{
        'col-8': showTemplates && !occurrence.isCompleted,
        'col-12': occurrence.isCompleted,
      }"
      style="margin-left: -10px"
    >
      <div class="overview-label mb-2">Task Overview</div>

      <div v-for="boardInfo in soBoards" :key="boardInfo.boardId">
        <div v-if="boardInfo.items && boardInfo.items.length">
          <div class="mt-4 label w-100 bg-active">
            <strong>{{ boardInfo.boardTitle }}</strong>
            <hr />
          </div>

          <div class="d-flex">
            <div class="mr-4 containers" v-for="list in boardLists" :key="list">
              <div v-for="item in filteredBoardItems(list, boardInfo.items, boardInfo.boardId)" :key="item.id">
                <div v-if="item">
                  <task-card :item="item" :isSO="true" class="m-1"></task-card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showNoAvailableTasks">
        <div class="no-available-items">There are no available Tasks</div>
      </div>
    </div>

    <router-view></router-view>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import { BoardItemCreateFromTemplateDto, BoardItemDto, BoardItemStatusDto } from '@/apiclient/apiclient_generated';
  import TaskCard from '@/components/TaskCard.vue';
  import lodash from 'lodash';
  import { UserModule, UserStore } from '@/store/user';

  @Component({
    components: {
      TaskCard,
    },
  })
  export default class SOTaskGeneration extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private userStore: UserModule = UserStore;
    private search = '';
    private showTemplates = true;
    private openedTemplates = [];
    private loadingTemplateIds = [];

    get boardLists(): BoardItemStatusDto[] {
      return [BoardItemStatusDto.Planned, BoardItemStatusDto.InProgress, BoardItemStatusDto.Done];
    }

    private isOpened(boardId, id) {
      return this.openedTemplates.some((_) => _.boardId === boardId && _.id === id);
    }

    toggleTemplate(boardId, id) {
      const index = this.openedTemplates.findIndex((_) => _.boardId === boardId && _.id === id);

      if (index >= 0) {
        this.openedTemplates.splice(index, 1);
      } else {
        this.openedTemplates.push({ boardId, id });
      }
    }

    get showNoAvailableTasks() {
      return !this.soBoards || !this.soBoards.length || this.soBoards.every((_) => !_.items || !_.items.length);
    }

    async mounted() {
      this.store.clearData();

      await this.store.getTemplates(this.$route.params.occId);
      await this.store.getSOBoards(this.$route.params.occId);
      await this.store.getActiveAndArchivedOccurrences();
    }

    get occurrence() {
      return this.store.currentOccurrence;
    }

    get soBoards() {
      const boards = this.store.soBoards;

      boards.forEach((b: any) => {
        const soBoard: any = this.store.soTemplates.find((_) => _.boardId === b.boardId);
        b.sortOrder = soBoard?.sortOrder;
      });

      return this.lodash.orderBy(boards, 'boardTitle');
    }

    get templates(): any[] {
      const soTemplates: any = this.store.soTemplates;
      const searchTemplate = this.search.toLowerCase();

      for (let bi = 0; bi < soTemplates.length; bi++) {
        const board = soTemplates[bi];

        const filteredTemplates = this.search.length
          ? board.templates.filter(
              (_) => _.title.toLowerCase().includes(searchTemplate) || _.description.toLowerCase().includes(searchTemplate)
            )
          : board.templates;

        board.filteredTemplates = filteredTemplates;
      }

      return lodash.orderBy(soTemplates, 'boardTitle');
    }

    async createBoardItem(boardId, templateId, flightLegEventId) {
      this.loadingTemplateIds.push(templateId);

      const popover: any = this.$refs['createItemFromTemplateRef_' + templateId + '_' + boardId];
      if (popover && popover[0]) {
        popover[0].$emit('close');
      }

      await this.store.createSpecialOccurrenceBoardItem({
        specialOccurrenceId: this.$route.params.occId,
        templateId: templateId,
        flightLegEventId: flightLegEventId,
      } as BoardItemCreateFromTemplateDto);

      this.loadingTemplateIds = this.loadingTemplateIds.filter((_) => _ != templateId);
    }

    filteredBoardItems(status: BoardItemStatusDto, items: BoardItemDto[]) {
      return items.filter((_) => _.boardItemStatus === status);
    }

    filteredSelectedFlights(flights) {
      return flights ? lodash.orderBy(flights, 'scheduledTimeOfDeparture') : [];
    }

    addNewBoardItem(boardId) {
      this.$router.push({ name: 'so-item', params: { itemid: 'new', board: boardId, occId: this.$route.params.occId } });
    }
  }
</script>

<style scoped lang="scss">
  .templates-vertical-panel {
    margin-top: 280px;
    margin-right: -17em;
    padding-bottom: 0;
    margin-bottom: 0;
    -webkit-transform: rotate(-90deg);
    -webkit-transform-origin: left top;
    -moz-transform: rotate(-90deg);
    -moz-transform-origin: left top;
    -ms-transform: rotate(-90deg);
    -ms-transform-origin: left top;
    -o-transform: rotate(-90deg);
    -o-transform-origin: left top;
    transform: rotate(-90deg);
    transform-origin: left top;
    white-space: nowrap;
  }

  .create-bi {
    margin-bottom: 0.5em;
    min-width: 100px;
  }

  .so-overview {
    flex-wrap: wrap;
    margin-left: -15px;

    .no-available-items {
      margin-top: 15px;
      font-weight: bold;
      font-size: 1.2rem;
    }

    .input-sm {
      height: 25px;
      font-size: 12px;
      line-height: 20px;
    }

    .overview-label {
      font-weight: bold;
      font-size: 1.1rem;
    }

    .headers {
      font-size: 1.1rem;
      padding: 4px 0;

      font-weight: bold;
      text-align: center;
      margin-left: 8px;
      margin-right: 8px;
      border-radius: 4px 4px 0 0;
      background: darken(#eaeaea, 8%);
    }

    .containers {
      padding: 5px;
      padding-bottom: 0;
      width: 330px;
      min-width: 300px;
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
      min-height: 50px;
      background: #eaeaea;
      border-radius: 4px;
      max-height: calc(90vh - 50px);
      overflow-y: auto;
      overflow-x: auto;
    }

    .containers::-webkit-scrollbar {
      width: 8px;
    }

    .containers::-webkit-scrollbar-track {
      background: #ddd;
    }

    .containers::-webkit-scrollbar-thumb {
      background: #999;
      border: 2px solid #ddd;
    }
  }

  .create-template-btn {
    padding: 0.1rem 0.5rem !important;
    height: 15px;
    align-self: center;
    min-width: 70px;
    font-size: 0.8rem;
  }

  .so-template {
    font-size: 0.8rem;
    border: solid 1px #ccc;
    outline: none;
    margin-bottom: 0.5em;

    box-shadow: 0 1px 0 rgba(9, 30, 66, 0.25);
    background: white;
    transition: all 0.25s ease-in-out;
    z-index: 1000;
    border-radius: 3px;

    .so-template-header {
      background-color: lighten(#eaeaea, 7%);
      padding: 3px;
      font-weight: bold;
      border-bottom: solid 1px darken(#eaeaea, 2%);

      .badge {
        font-size: 0.65rem;
      }
    }

    .so-template-content {
      padding: 7px;

      .validtimes {
        margin-top: 10px;
        color: white;

        div {
          padding: 5px;
          background: #357ebd;
          border-radius: 4px;
        }
      }

      .checklist-items {
        margin-top: -5px;
        .checklist-item {
          .checkbox-label {
            padding-top: 2px;
          }
        }
      }
    }

    .description {
      font-size: 0.8rem;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      width: 200px;
    }

    .label {
      font-weight: 600;
      color: #888 !important;
      text-transform: uppercase;
      margin-bottom: 8px !important;
    }
  }

  .wide-popover {
    max-width: 700px;
  }

  .board-label {
    margin: 0 !important;
  }

  .create-board-item-btn {
    padding: 0.3rem 0.5rem !important;
    height: 19px;
  }
</style>
